// src/utils/ImagePreloader.ts

interface PreloadOptions {
    onFirstScreenComplete?: () => void;
    onAllComplete?: () => void;
    onProgress?: (progress: number) => void;
    onError?: (error: Error) => void;
    onImageLoad?: (src: string) => void;
    shouldPreload?: (src: string) => boolean; // 新增回调
}

class ImagePreloader {
    private firstScreenImages: string[] = [];
    private remainingImages: string[] = [];
    private loadedCount = 0;
    private totalCount = 0;
    private static loadedImages = new Set<string>(); // 新增全局Set

    constructor(
        firstScreenImages: string[] = [],
        remainingImages: string[] = []
    ) {
        this.firstScreenImages = firstScreenImages;
        this.remainingImages = remainingImages;
        this.totalCount = firstScreenImages.length + remainingImages.length;
    }

    preload(options: PreloadOptions = {}) {
        const {
            onFirstScreenComplete,
            onAllComplete,
            onProgress,
            onError,
            onImageLoad,
            shouldPreload // 新增回调
        } = options;

        // 首屏资源加载
        Promise.all(
            this.firstScreenImages.map((src) => {
                if (shouldPreload && !shouldPreload(src)) {
                    return Promise.resolve(); // 跳过已加载的图片
                }

                return this.loadImage(src, onProgress, onError, onImageLoad);
            })
        )
            .then(() => {
                onFirstScreenComplete?.();

                // 加载剩余资源
                return Promise.all(
                    this.remainingImages.map((src) => {
                        if (shouldPreload && !shouldPreload(src)) {
                            return Promise.resolve(); // 跳过已加载的图片
                        }

                        return this.loadImage(src, onProgress, onError, onImageLoad);
                    })
                );
            })
            .then(() => {
                onAllComplete?.();
            })
            .catch((error) => {
                onError?.(error);
            });
    }

    private loadImage(
        src: string,
        onProgress?: (progress: number) => void,
        onError?: (error: Error) => void,
        onImageLoad?: (src: string) => void
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            if (ImagePreloader.loadedImages.has(src)) {
                resolve(); // 如果图片已经加载过，直接resolve

                return;
            }

            const img = new Image();

            img.onload = () => {
                ImagePreloader.loadedImages.add(src); // 加载成功后添加到Set
                this.loadedCount++;
                onProgress?.(this.loadedCount / this.totalCount);
                onImageLoad?.(src); // 单张图片加载完成回调
                resolve();
            };

            img.onerror = () => {
                const error = new Error(`Failed to load image: ${src}`);

                onError?.(error);
                reject(error);
            };

            const webpSrc = src.startsWith('data:') ? src : `${src}?x-s3-process=image/format,webp`;

            img.src = webpSrc;
        });
    }
}

export default ImagePreloader;
